import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import axios from "axios";
import API from "@/utils/api";
import RiderLayout from "./RiderLayout";
import { Navigation, MapPin, Clock, DollarSign, ChevronRight } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Leaflet custom icons
const iconPickup = new L.Icon({
    iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-orange.png",
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png",
    iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41]
});

export default function RiderJobs() {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const audioRef = React.useRef(new Audio("/sounds/conexo_alert.mp3"));

    const fetchAvailableJobs = async () => {
        try {
            const token = localStorage.getItem("rider_token");
            if (!token) return;
            const res = await axios.get(`${API}/rider/jobs/available`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // Si hay más pedidos que antes, sonar la alerta
            if (res.data.length > jobs.length) {
                console.log("¡Nuevo pedido disponible! Sonando alerta...");
                audioRef.current.play().catch(e => console.log("Audio play failed:", e));
                toast.info("¡Nuevo pedido disponible!", {
                    icon: "🔔",
                    style: { background: "#0ea5e9", color: "white", fontWeight: "bold" }
                });
            }

            setJobs(res.data);
        } catch (error) {
            console.error("Error fetching available jobs", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAvailableJobs();
        const interval = setInterval(fetchAvailableJobs, 10000); // Poll every 10s
        return () => clearInterval(interval);
    }, [jobs.length]); // Re-bind effect when jobs length changes to compare

    const acceptJob = async (jobId) => {
        try {
            const token = localStorage.getItem("rider_token");
            await axios.post(`${API}/rider/jobs/${jobId}/accept`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("¡Pedido aceptado!");
            fetchAvailableJobs();
        } catch (error) {
            toast.error(error.response?.data?.detail || "Alguien más tomó este pedido");
            fetchAvailableJobs();
        }
    };

    return (
        <RiderLayout>
            <div className="p-4 space-y-4">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-sm font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
                        <MapPin size={16} className="text-primary" />
                        Pedidos Disponibles
                    </h2>
                    <span className="text-primary bg-primary/10 px-3 py-1 rounded-full text-xs font-black">
                        {jobs.length} Nuevos
                    </span>
                </div>

                {/* Map of Available Jobs */}
                {!loading && jobs.length > 0 && (
                    <div className="h-48 w-full bg-slate-900 rounded-3xl overflow-hidden border border-white/10 shadow-xl mb-6 relative">
                        <MapContainer
                            center={[jobs[0].pickup_lat, jobs[0].pickup_lng]}
                            zoom={13}
                            scrollWheelZoom={false}
                            className="h-full w-full z-0"
                            zoomControl={false}
                            attributionControl={false}
                        >
                            <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" attribution="" />
                            {jobs.map(job => (
                                <Marker
                                    key={job.job_id}
                                    position={[job.pickup_lat, job.pickup_lng]}
                                    icon={iconPickup}
                                >
                                    <Popup>
                                        <div className="text-black p-1">
                                            <p className="font-bold text-xs">{job.restaurant_name}</p>
                                            <p className="text-[10px]">{job.distance_km.toFixed(1)} km de entrega</p>
                                        </div>
                                    </Popup>
                                </Marker>
                            ))}
                        </MapContainer>
                        <div className="absolute top-2 right-2 bg-black/60 backdrop-blur-md px-2 py-1 rounded-full text-[8px] font-black text-white/70 uppercase tracking-tighter z-[400]">
                            Radar de pedidos
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="space-y-4">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="h-32 bg-white/5 border border-white/5 rounded-3xl animate-pulse" />
                        ))}
                    </div>
                ) : jobs.length === 0 ? (
                    <div className="text-center bg-white/5 border border-white/5 rounded-3xl p-10 mt-10 backdrop-blur-md">
                        <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4 border border-white/10">
                            <Navigation size={24} className="text-slate-500 opacity-50" />
                        </div>
                        <h3 className="text-lg font-bold text-white mb-2">Aún no hay pedidos</h3>
                        <p className="text-sm text-slate-400 font-medium">Buscando oportunidades cerca de ti en tiempo real...</p>

                        <div className="flex justify-center gap-2 mt-6">
                            <div className="w-2 h-2 rounded-full bg-primary animate-ping" />
                            <div className="w-2 h-2 rounded-full bg-primary/50 animate-ping" style={{ animationDelay: '0.2s' }} />
                            <div className="w-2 h-2 rounded-full bg-primary/20 animate-ping" style={{ animationDelay: '0.4s' }} />
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {jobs.map((job) => (
                            <div key={job.job_id} className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-3xl p-5 transition-all shadow-lg relative overflow-hidden group">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 blur-2xl rounded-full group-hover:bg-primary/20 transition-all -z-10" />

                                <div className="flex justify-between items-start mb-4">
                                    <div className="pr-4">
                                        <h3 className="font-bold text-white text-lg tracking-tight leading-tight">{job.restaurant_name}</h3>
                                        <p className="text-[10px] uppercase font-black tracking-widest text-slate-500 line-clamp-1 mt-1">
                                            A {job.driver_to_pickup_km.toFixed(1)} km de distancia
                                        </p>
                                    </div>
                                    <div className="bg-primary/20 text-primary border border-primary/30 rounded-xl px-3 py-1.5 flex flex-col items-center">
                                        <span className="text-[10px] font-black uppercase mb-[-2px]">Total</span>
                                        <span className="font-black text-sm tracking-tight">{job.distance_km.toFixed(1)} km</span>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3 mb-5">
                                    <div className="bg-black/40 border border-white/5 rounded-xl p-3 flex items-center gap-2">
                                        <Clock size={16} className="text-blue-400 shrink-0" />
                                        <div className="flex flex-col">
                                            <span className="text-[9px] uppercase font-black text-slate-500 tracking-wider">Tiempo Est.</span>
                                            <span className="text-sm font-bold text-white">~{job.estimated_time_mins} min</span>
                                        </div>
                                    </div>
                                    <div className="bg-black/40 border border-white/5 rounded-xl p-3 flex items-center gap-2">
                                        <DollarSign size={16} className="text-green-400 shrink-0" />
                                        <div className="flex flex-col">
                                            <span className="text-[9px] uppercase font-black text-slate-500 tracking-wider">Pago base</span>
                                            <span className="text-sm font-bold text-green-400">${job.delivery_fee?.toFixed(2) || "3.50"}</span>
                                        </div>
                                    </div>
                                </div>

                                <button
                                    onClick={() => acceptJob(job.job_id)}
                                    className="w-full bg-primary hover:bg-blue-600 text-white font-black uppercase tracking-widest py-3 rounded-xl transition-all shadow-[0_4px_15px_rgba(59,130,246,0.3)] active:scale-[0.98]"
                                >
                                    Aceptar Pedido
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </RiderLayout>
    );
}
